import {
  forwardRef,
  type ReactElement,
  type Ref,
  useId,
  useMemo,
  useRef,
} from 'react';
import { PropsValue, SingleValue } from 'react-select';

import { VisuallyHidden } from '../../../core/components/visually-hidden/VisuallyHidden.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import type { FormControlState } from '../../../core/types/form-control-state.js';
import { useFormFieldValidation } from '../../forms-core/hooks/useFormFieldValidation.js';
import type { BaseOption } from '../types.js';

//#region Typings
/** Accepted properties for the HiddenSelect component */
export interface HiddenSelectV2Props<OptionValue> {
  ref: Ref<HTMLSelectElement>;
  name?: string;
  selectedValues?: PropsValue<OptionValue>;
  required?: boolean;
  disabled?: boolean;
  ariaDisabled?: boolean;
  controlState?: FormControlState;
}
//#endregion

/** HiddenSelect component */
export const HiddenSelectV2: <OptionValue>(
  props: HiddenSelectV2Props<OptionValue>,
) => /* @__PURE__ */ ReactElement | null = forwardRef(
  /* eslint-disable @typescript-eslint/no-unnecessary-type-constraint */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <OptionValue extends any>(
    props: HiddenSelectV2Props<OptionValue>,
    forwardedRef: HiddenSelectV2Props<OptionValue>['ref'],
  ) => {
    const {
      name,
      selectedValues,
      required,
      disabled,
      ariaDisabled,
      controlState,
    } = props;
    const multiple = Array.isArray(selectedValues);

    const id = useId();
    const ref = useRef<HTMLSelectElement | null>(null);
    const { ariaProps, onBlur, onChange, onInvalid } = useFormFieldValidation(
      ref,
      id,
    );

    const selectedValuesArray = useMemo(() => {
      // Use textValue if it's defined. If not, use label (only if it's a string). Otherwise, use the value property.
      const extractValue = (
        val: SingleValue<BaseOption<OptionValue>>,
      ): string => {
        return val?.textValue ?? '';
      };

      if (multiple) {
        return selectedValues?.length > 0
          ? selectedValues.map(extractValue)
          : [];
      }

      if (selectedValues) {
        return [
          extractValue(
            selectedValues as unknown as SingleValue<BaseOption<OptionValue>>,
          ),
        ];
      }

      return [];
    }, [multiple, selectedValues]);

    const value = useMemo(() => {
      if (multiple) {
        return selectedValuesArray;
      }
      // use string value or empty string as array values are not allowed for single select
      return selectedValues
        ? `${
            (selectedValues as unknown as SingleValue<BaseOption<OptionValue>>)
              ?.value
          }`
        : '';
    }, [multiple, selectedValues, selectedValuesArray]);

    return (
      <VisuallyHidden>
        <select
          ref={useMergeRefs([forwardedRef, ref])}
          name={name}
          multiple={multiple}
          tabIndex={-1}
          required={required}
          disabled={disabled}
          aria-disabled={ariaDisabled}
          aria-hidden
          value={value && value.length !== 0 ? value : undefined}
          id={id}
          onChange={() => undefined}
          {...(controlState
            ? {}
            : { ...ariaProps, onBlur, onChange, onInvalid })}
        >
          {selectedValuesArray.map((val, key) => {
            return <option value={`${val}`} key={`${key}-${val}`} />;
          })}
        </select>
      </VisuallyHidden>
    );
  },
);

(
  HiddenSelectV2 as typeof HiddenSelectV2 & { displayName: string }
).displayName = 'HiddenSelectV2';
